import usePublicEventService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import PublicSeatingCart from "@app-contexts/public/features/seating/components/SeatingCart/SeatingCart.web";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import { ASSIGNED_SEATING_MODES } from "@shared/constants";
import useAssignedSeatingHelpers from "@shared/hooks/seating/useAssignedSeatingHelpers/useAssignedSeatingHelpers";
import { useSeatingHeight } from "@shared/hooks/seating/useSeatingHeight";
import useWindowSize from "@shared/hooks/useWindowSize";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import usePublicMembershipGroupsService from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import BasicSeatingMap from "@shared/modules/seating/components/BasicSeatingMap/BasicSeatingMap.web";
import SeatingLayout from "@shared/modules/seating/components/SeatingLayout/SeatingLayout.web";
import SeatRemoveModal from "@shared/modules/seating/components/SeatRemoveModal/SeatRemoveModal.web";
import SeatSelectModal from "@shared/modules/seating/components/SeatSelectModal/SeatSelectModal.web";
import SidebarSeatingContainer from "@shared/modules/seating/components/SidebarSeatingContainer/SidebarSeatingContainer.web";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import BestAvailableSeatingSlide from "@shared/modules/seating/features/BestAvailable/components/BestAvailableSeatingSlide/BestAvailableSeatingSlide.web";
import { BestAvailableTypeSelectionDialog } from "@shared/modules/seating/features/BestAvailable/components/BestAvailableTypeSelectionDialog/BestAvailableTypeSelectionDialog.web";
import { BestAvailableQueryActions } from "@shared/modules/seating/features/BestAvailable/hooks/useBestAvailableQueryParams";
import useBestAvailableSeatingData from "@shared/modules/seating/features/BestAvailable/hooks/useBestAvailableSeatingData";
import { getPurchaseLimitsFromSeatingItemArray } from "@shared/modules/seating/features/BestAvailable/utils/utils";
import SeatingService from "@shared/modules/seating/services/SeatingService";
import breakpoints from "@shared/theme/props/breakpoints";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { useEffect } from "react";
import ItemSelectModal from "../ItemSelectModal/ItemSelectModal.web";
import BestAvailableBasketFooter from "../BestAvailableBasketFooter/BestAvailableBasketFooter.web";
import { PublicAssignedSeatingProps } from "./PublicAssignedSeating.props";

/**
 * Height constraint for fit-content parent containers (e.g., embedded widgets).
 * Prevents infinite height growth when best available seating creates a circular
 * height dependency with percentage-based child heights.
 * If max height is used by multiple components needing different values,
 * move this to be accepted via props.
 */
const FIT_CONTENT_MAX_HEIGHT = { base: "50vh", sm: "550px" } as const;

/**
 * Determines the appropriate maxHeight for the seating container.
 * Only applies fit-content constraint when both conditions are met:
 * 1. Parent uses fit-content (isFitContent)
 * 2. Best available seating is enabled (creates the problematic height chain)
 */
function getMaxHeight({
  isFitContent,
  isBestAvailableEnabled,
  isSlideOpen,
}: {
  isFitContent: boolean;
  isBestAvailableEnabled: boolean;
  isSlideOpen: boolean;
}): typeof FIT_CONTENT_MAX_HEIGHT | { base: string; sm: string } | string {
  // No constraint needed when best available seating is disabled
  if (!isBestAvailableEnabled) {
    return "100%";
  }

  // Apply fit-content constraint to prevent infinite growth in embedded contexts
  if (isFitContent) {
    return FIT_CONTENT_MAX_HEIGHT;
  }

  // Default behavior for non-embedded contexts with best available enabled
  if (isSlideOpen) {
    return { base: "50vh", sm: "100%" };
  }

  return "100%";
}

const PublicAssignedSeating = ({
  spaceData,
  venueData,
  seatingObject,
  voucherData,
  packageItemGroupParent,
  hideCheckoutButton = false,
  hideDrawer = false,
  onCheckout,
  withBestAvailableSeating = true,
  isFitContent = false,
}: PublicAssignedSeatingProps) => {
  // Public basket is the default type
  const { isExpired } = useBasketService();
  const { width } = useWindowSize();
  const isMobile = typeof width === "number" && width < breakpoints.sm;

  const {
    seatingServiceState,
    isModalOutsideMap, // do not open modals on this page if modal is opened from another component (ex: mobile cart)
    modalValues,
    resetMapSelections,
    bestAvailableSeatingQueryParams,
    hasSeatsWithMultipleTypes,
    hasPasswordProtectedSeats,
    updateBestAvailableSeatingQueryParams,
    isLoadingMap,
    seatingItemArray,
    basketType,
    typesOnMap,
    disableBestAvailableSeating,
    paymentType,
    purchaseSourcePlatform,
  } = useSeatingState();

  const { modalOpen, resolveModal, rejectModal, seatsToAdd, seatsToRemove } =
    modalValues;

  const {
    itemsOnSeat,
    itemSelectModalOpen,
    ticketAuthArray,
    onPermissionSelect,
    resolveItemSelect,
    rejectItemSelect,
    updateTicketAuthArray,
    onSegmentSelect,
  } = useAssignedSeatingHelpers();

  const { bestAvailableData, isBestAvailableSeatingLoading } =
    useBestAvailableSeatingData({
      allInPricingEnabled: venueData.all_in_pricing_is_enabled,
      currency: venueData.currency,
      venueId: venueData.id,
      paymentType,
      purchaseSourcePlatform,
    });

  const {
    isOpen: isBestAvailableSeatingSlideOpen,
    onClose: onCloseBestAvailableSeatingSlide,
    onOpen: onOpenBestAvailableSeatingSlide,
  } = useDisclosure({
    defaultIsOpen: true,
  });
  const { isOpen, onClose, onOpen } = useDisclosure({
    defaultIsOpen: !disableBestAvailableSeating && withBestAvailableSeating,
    onClose: onOpenBestAvailableSeatingSlide, // when closing quantity select, open the best available seating drawer
  });

  // de-select all seats/locations when the cart expires
  useEffect(() => {
    if (isExpired) {
      resetMapSelections();
    }
  }, [isExpired, resetMapSelections]);

  useEffect(() => {
    if (spaceData && spaceData.membership_benefit) {
      updateBestAvailableSeatingQueryParams({
        type: BestAvailableQueryActions.MEMBERSHIP_BENEFIT,
        payload: { membership_benefit: spaceData.membership_benefit },
      });
      updateBestAvailableSeatingQueryParams({
        type: BestAvailableQueryActions.ALL_PERMISSIONS,
        payload: { allPermissions: false },
      });
    }
  }, [spaceData, updateBestAvailableSeatingQueryParams]);

  // set callback to handle seat clicks
  SeatingService.setSelectCallBack(onPermissionSelect);
  SeatingService.setSegmentSelectCallback(onSegmentSelect);

  // Trigger window resize event when drawer state changes to re-center the stage
  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [isBestAvailableSeatingSlideOpen]);

  const maxMapHeight = useSeatingHeight({
    includeSlide: true,
    mobileOnly: true,
    isSlideOpen: isBestAvailableSeatingSlideOpen,
    isMobile,
  });

  const { min, max } = getPurchaseLimitsFromSeatingItemArray(
    seatingItemArray,
    basketType
  );

  return (
    <Flex
      flex="1"
      height="100%"
      // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
      minHeight="0" // needs to be 0 for embedded to not grow infinitely
      flexDirection="column"
      position="relative"
    >
      <Flex
        flex="1"
        // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
        minHeight="0" // needs to be 0 for embedded to not grow infinitely
        flexDirection="column"
        maxHeight={getMaxHeight({
          isFitContent,
          isBestAvailableEnabled: !disableBestAvailableSeating,
          isSlideOpen: isBestAvailableSeatingSlideOpen,
        })}
      >
        <SeatingLayout
          maxMapHeight={
            !disableBestAvailableSeating &&
            isBestAvailableSeatingSlideOpen &&
            isMobile
              ? maxMapHeight
              : undefined
          }
          mapElements={
            <>
              {/* Ticket selection and removal modals */}
              {/* Only display modals when called from the map component itself */}
              {!isModalOutsideMap && (
                <>
                  {/* Displays ticket types to the user when multiple tickets are assigned to a seat or location */}
                  {itemsOnSeat && voucherData && (
                    <ItemSelectModal
                      isOpen={itemSelectModalOpen}
                      onClose={rejectItemSelect}
                      resolve={resolveItemSelect}
                      seatingEvent={seatingObject}
                      venueData={venueData}
                      itemsOnSeat={itemsOnSeat}
                      voucherData={voucherData}
                      ticketAuthArray={ticketAuthArray}
                      updateTicketAuthArray={updateTicketAuthArray}
                    />
                  )}
                  {/* Handles adding isolated, full-purchase, or resale seats to the cart */}
                  {seatsToAdd && (
                    <SeatSelectModal
                      isOpen={modalOpen}
                      onClose={rejectModal}
                      resolve={resolveModal}
                      seatsToAdd={seatsToAdd}
                      itemGroupParent={packageItemGroupParent}
                      venueData={venueData}
                    />
                  )}
                  {/* Handles removing isolated, full-purchase, resale seats, or minimum-purchase seats from the cart */}
                  {seatsToRemove && (
                    <SeatRemoveModal
                      isOpen={modalOpen}
                      onClose={rejectModal}
                      resolve={resolveModal}
                      seatsToRemove={seatsToRemove}
                      itemGroupParent={packageItemGroupParent}
                    />
                  )}
                </>
              )}
              <BasicSeatingMap
                spaceData={spaceData}
                venue={venueData}
                mapMode={ASSIGNED_SEATING_MODES.EVENT_DETAIL_VIEW.id}
                mapItemType={seatingServiceState.mapItemType}
                hideDrawer={hideDrawer}
                isBestAvailableListOpen={isBestAvailableSeatingSlideOpen}
                onToggleBestAvailableList={() => {
                  if (isBestAvailableSeatingSlideOpen) {
                    onCloseBestAvailableSeatingSlide();
                  } else {
                    onOpenBestAvailableSeatingSlide();
                  }
                }}
              />
            </>
          }
          sidebarElements={
            <>
              <SidebarSeatingContainer
                bestAvailableSeating={bestAvailableData}
                onContinue={onCheckout}
                cartComponent={
                  <PublicSeatingCart
                    venueData={venueData}
                    onCheckoutCb={onCheckout}
                    hideCheckoutButton={hideCheckoutButton}
                  />
                }
                isLoading={isBestAvailableSeatingLoading}
                disableBestAvailableSeating={
                  !withBestAvailableSeating || disableBestAvailableSeating
                }
                onOpenTypeQuantitySelect={onOpen}
                hideCheckoutButton={hideCheckoutButton}
                basketFooter={<BestAvailableBasketFooter />}
              />
            </>
          }
        />
      </Flex>
      {!isLoadingMap && typesOnMap && !disableBestAvailableSeating && (
        <>
          <BestAvailableTypeSelectionDialog
            isOpen={
              isOpen && (hasSeatsWithMultipleTypes || hasPasswordProtectedSeats)
            }
            onClose={onClose}
            updateBestAvailableQueryParams={
              updateBestAvailableSeatingQueryParams
            }
            eventsService={usePublicEventService}
            membershipService={usePublicMembershipGroupsService}
            slug={seatingObject.slug}
            initialSelectedTicketTypes={
              bestAvailableSeatingQueryParams.ticket_types
            }
            initialSelectedMembershipLevels={
              bestAvailableSeatingQueryParams.membership_levels
            }
            isMembership={Boolean(spaceData?.membership_benefit)}
            isEvent={Boolean(spaceData?.event)}
            typesOnMap={typesOnMap}
          />
        </>
      )}

      {!isLoadingMap && typesOnMap && !disableBestAvailableSeating && (
        <BestAvailableSeatingSlide
          bestAvailableSeating={bestAvailableData}
          isOpen={isBestAvailableSeatingSlideOpen}
          onOpen={onOpenBestAvailableSeatingSlide}
          onContinue={onCheckout}
          onClose={onCloseBestAvailableSeatingSlide}
          minQuantity={min}
          maxQuantity={max}
          isLoading={isBestAvailableSeatingLoading}
          onUpdateQuantity={updateBestAvailableSeatingQueryParams}
          currentQuantity={bestAvailableSeatingQueryParams.quantity}
          onOpenTicketTypeSelectionDialog={onOpen}
          hideCheckoutButton={hideCheckoutButton}
          basketFooter={<BestAvailableBasketFooter />}
        />
      )}
    </Flex>
  );
};

export default PublicAssignedSeating;
